import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyMissionEvent, applyStepEvent, autoTick, currentStep, freshProgress, levelUpsOpen, type MissionStep, type StepEvent, type StepPanel } from "@buildlab/shared/steps"; // a subpath: the package root ships the screening lists
import { api, type AssignmentInfo, type StepState } from "./api";
import { useRefreshCoins } from "./me";

// Missions as self-ticking steps (Review 17, D45). The workspace keeps one controller per project:
// it hears what the student does (played, sent, opened a tab, edited, checked tests, made a save
// point, submitted, answered the Press Play question), ticks the current step when its trigger
// happens (pure logic in @buildlab/shared/steps), and saves the ticks with the project.
// Components deep in the tree (TestLab, Submit, Checkpoints) report with emitStep().
//
// Level-ups (decision 101) run on the same engine: once every step is done, steps are skipped, or
// the project was submitted, events go to the level-ups instead, in order, one at a time. An event
// ticks one list only, so the step that finishes the mission never also ticks a level-up, and
// "approve a level 2 plan" needs a new approval. The Worker pays coins for each new one.

const EVENT = "buildlab-step";

/** Reports something the student did to the open project's mission. Safe to call anywhere. */
export function emitStep(e: StepEvent): void {
  try {
    window.dispatchEvent(new CustomEvent<StepEvent>(EVENT, { detail: e }));
  } catch {}
}

export interface MissionController {
  steps: MissionStep[];
  /** Step ids and level-up ids done. */
  done: string[];
  current: MissionStep | null;
  /** The lesson's level-ups (empty for most lessons), whether they're open, and the current one. */
  levelUps: MissionStep[];
  levelUpsOpen: boolean;
  levelUp: MissionStep | null;
  /** Coins the last level-up earned while the student was here (for "+5 coins!"), or null. */
  earned: { coins: number; at: number } | null;
  skipped: boolean;
  prediction: string | null;
  /** The panel to light up (null when skipped, finished or no panel). */
  guide: StepPanel | null;
  report: (e: StepEvent) => void;
  setSkipped: (on: boolean) => void;
  predict: (choice: string) => void;
}

export function useMission(projectId: string, assignment: AssignmentInfo | null, initial: StepState | null): MissionController | null {
  const steps = useMemo(() => assignment?.steps ?? [], [assignment]);
  const levelUps = useMemo(() => assignment?.levelUps ?? [], [assignment]);
  const refreshCoins = useRefreshCoins();
  const [done, setDone] = useState<string[]>(initial?.done ?? []);
  const [submitted, setSubmitted] = useState(initial?.submitted ?? false);
  const [earned, setEarned] = useState<{ coins: number; at: number } | null>(null);
  const skippedRef = useRef(initial?.skipped ?? false);
  const submittedRef = useRef(initial?.submitted ?? false);
  const [skipped, setSkippedState] = useState(initial?.skipped ?? false);
  const [prediction, setPrediction] = useState<string | null>(initial?.prediction ?? null);
  const progress = useRef(freshProgress(initial?.done ?? []));
  const predicted = useRef(!!initial?.prediction);

  // A new project (or its state arriving) starts from what the server has.
  useEffect(() => {
    progress.current = freshProgress(initial?.done ?? []);
    predicted.current = !!initial?.prediction;
    setDone(progress.current.done);
    setSkippedState(initial?.skipped ?? false);
    setPrediction(initial?.prediction ?? null);
    skippedRef.current = initial?.skipped ?? false;
    submittedRef.current = initial?.submitted ?? false;
    setSubmitted(submittedRef.current);
    setEarned(null);
  }, [projectId, initial]);

  const save = useCallback(
    (body: { done?: string[]; skipped?: boolean; prediction?: string }) => {
      api
        .saveSteps(projectId, body)
        .then((r) => {
          // A level-up paid coins (decision 101): say so, and show the new count by Send.
          if (r.earned) {
            setEarned({ coins: r.earned, at: Date.now() });
            refreshCoins();
          }
        })
        .catch(() => {}); // the ticks on screen stay; the next tick sends them all again
    },
    [projectId, refreshCoins],
  );

  const settle = useCallback(
    (ticked: string[], extra: { prediction?: string } = {}) => {
      const auto = autoTick(steps, progress.current, { predicted: predicted.current });
      progress.current = auto.progress;
      const all = [...ticked, ...auto.ticked];
      if (all.length || extra.prediction) {
        setDone(progress.current.done);
        save({ done: progress.current.done, ...extra });
      }
    },
    [steps, save],
  );

  const report = useCallback(
    (e: StepEvent) => {
      if (!steps.length) return;
      // The steps, or the level-ups once they're open (decision 101): one list per event.
      const r = applyMissionEvent(steps, levelUps, progress.current, e, { skipped: skippedRef.current, submitted: submittedRef.current });
      progress.current = r.progress;
      if (r.ticked) settle([r.ticked]);
      if (e.kind === "submitted" && !submittedRef.current) {
        submittedRef.current = true; // after this event: Submit opens the level-ups for the next one
        setSubmitted(true);
      }
    },
    [steps, levelUps, settle],
  );

  // Ticks from anywhere in the workspace.
  useEffect(() => {
    const on = (ev: Event) => report((ev as CustomEvent<StepEvent>).detail);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, [report]);

  // A prediction already made (an earlier visit) ticks its step when it comes up.
  useEffect(() => {
    settle([]);
  }, [settle]);

  const predict = useCallback(
    (choice: string) => {
      if (predicted.current) return;
      predicted.current = true;
      setPrediction(choice);
      const r = applyStepEvent(steps, progress.current, { kind: "predicted" });
      progress.current = r.progress;
      settle(r.ticked ? [r.ticked] : [], { prediction: choice });
    },
    [steps, settle],
  );

  const setSkipped = useCallback(
    (on: boolean) => {
      setSkippedState(on);
      skippedRef.current = on;
      save({ skipped: on });
    },
    [save],
  );

  if (!assignment || steps.length === 0) return null;
  const current = currentStep(steps, done);
  const open = levelUpsOpen(steps, levelUps, { done, skipped, submitted });
  const levelUp = open ? currentStep(levelUps, done) : null;
  const guide = open ? (levelUp?.panel ?? null) : !skipped && current?.panel ? current.panel : null;
  return { steps, done, current, levelUps, levelUpsOpen: open, levelUp, earned, skipped, prediction, guide, report, setSkipped, predict };
}
